/**
 * HOOK: useTickets
 *
 * Gestiona el estado de tickets de soporte del usuario: conteo de abiertos,
 * detección de actualizaciones admin, y modales de tickets/soporte.
 * Extraído de App.jsx como parte de refactor convivencia.
 *
 * Convivencia: este hook corre en paralelo con el código inline de App.jsx.
 * Ambos se suscriben a Firestore onSnapshot — Firebase maneja múltiples
 * listeners sin duplicar llamadas.
 */

import { useState, useEffect } from 'react'
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '../firebase-config'

export function useTickets(uid, isUserAuthenticated) {
  const [openTickets, setOpenTickets] = useState(0)
  const [hasAdminUpdates, setHasAdminUpdates] = useState(false)
  const [ticketsSeenAt, setTicketsSeenAt] = useState(() => Number(localStorage.getItem('ticketsSeenAt') || 0))
  const [showSupportModal, setShowSupportModal] = useState(false)
  const [showTicketsModal, setShowTicketsModal] = useState(false)

  const toMillis = (value) => {
    if (value?.toDate) return value.toDate().getTime()
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? 0 : d.getTime()
  }

  /**
   * Suscripción Firestore a tickets del usuario autenticado
   * Refleja el mismo comportamiento que el useEffect inline en App.jsx
   */
  useEffect(() => {
    if (!isUserAuthenticated) {
      setOpenTickets(0)
      return undefined
    }
    if (!uid) {
      setOpenTickets(0)
      setHasAdminUpdates(false)
      return undefined
    }
    const q = query(
      collection(db, 'tickets'),
      where('userUid', '==', uid),
      limit(20)
    )
    const unsub = onSnapshot(
      q,
      (snap) => {
        const tickets = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        const abiertos = tickets.filter((ticket) => ticket.estado !== 'resuelto').length
        const alertasAdmin = tickets.some(
          (ticket) =>
            ticket.estado !== 'resuelto' &&
            ticket.lastMessageRole === 'admin' &&
            toMillis(ticket.lastMessageAt || ticket.updatedAt) > ticketsSeenAt
        )
        setOpenTickets(abiertos)
        setHasAdminUpdates(alertasAdmin)
      },
      () => {
        setOpenTickets(0)
        setHasAdminUpdates(false)
      }
    )
    return () => unsub()
  }, [isUserAuthenticated, uid, ticketsSeenAt])

  /**
   * Abrir centro de tickets: registra visto, limpia alerta admin, abre modal
   */
  const openTicketsCenter = () => {
    const now = Date.now()
    setTicketsSeenAt(now)
    localStorage.setItem('ticketsSeenAt', String(now))
    setHasAdminUpdates(false)
    setShowTicketsModal(true)
  }

  return {
    openTickets,
    setOpenTickets,
    hasAdminUpdates,
    setHasAdminUpdates,
    ticketsSeenAt,
    setTicketsSeenAt,
    showSupportModal,
    setShowSupportModal,
    showTicketsModal,
    setShowTicketsModal,
    openTicketsCenter,
  }
}
